import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import useAxios from '../utils/useAxios';
import { ShellContext } from './layout/AppShell';
import { TRUE_COLORS } from '../theme';

const DISMISS_KEY = 'true:getting-started-dismissed';

type Step = { key: string; title: string; description: string; cta: string; to: string; done: boolean };

/** Onboarding checklist for new organizations (mirrors ONBOARDING.md). Hides itself when complete or dismissed. */
const GettingStarted = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { organizationName } = React.useContext(ShellContext);

  const [status, setStatus] = React.useState<null | { locationConfigured: boolean; firstLocation?: number; products: number; members: number; receipts: number }>(null);
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  React.useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    (async () => {
      const list = async (url: string, params?: object): Promise<any[]> => {
        try {
          const { data } = await axios.get(url, { params });
          return Array.isArray(data) ? data : [];
        } catch {
          return [];
        }
      };
      const [locations, products, members, receipts] = await Promise.all([
        list('api/locations/'),
        list('api/products/'),
        list('management/members/'),
        list('api/receipts/', { topk: 1, compact: 1 }),
      ]);
      // New organizations get an automatic branch called "Default"; it counts once renamed or given an address.
      const locationConfigured = locations.some(l => l.name !== 'Default' || l.address);
      if (!cancelled) {
        setStatus({ locationConfigured, firstLocation: locations[0]?.id, products: products.length, members: members.length, receipts: receipts.length });
      }
    })();
    return () => { cancelled = true; };
  }, [axios, dismissed]);

  if (dismissed || !status) return null;

  const steps: Step[] = [
    {
      key: 'location', title: 'Configura tu sucursal', to: status.firstLocation ? `/admin/locations/${status.firstLocation}` : '/admin/organization', cta: 'Configurar sucursal',
      description: 'Ponle nombre y dirección; aparecerán en los tickets. El inventario se lleva por sucursal.', done: status.locationConfigured,
    },
    {
      key: 'products', title: 'Agrega tus productos', to: '/admin/add-product', cta: 'Agregar producto',
      description: 'Código de barras, precio y stock inicial. Se sincronizan con TRUE POS.', done: status.products > 0,
    },
    {
      key: 'pos', title: 'Conecta TRUE POS y haz tu primera venta', to: '/pos#descargar', cta: 'Descargar TRUE POS',
      description: 'Inicia sesión en la app de escritorio con esta cuenta y elige tu sucursal en Configuración.', done: status.receipts > 0,
    },
    {
      key: 'team', title: 'Invita a tu equipo', to: '/admin/organization', cta: 'Invitar',
      description: 'Tus cajeros entran a TRUE POS con su propia cuenta.', done: status.members > 1,
    },
  ];
  const completed = steps.filter(s => s.done).length;
  if (completed === steps.length) return null;
  const next = steps.find(s => !s.done);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* storage unavailable */ }
    setDismissed(true);
  };

  return (
    <Card sx={{ mb: 3, overflow: 'hidden' }}>
      <Box sx={{ backgroundImage: TRUE_COLORS.headerGradient, color: '#fff', px: 3, py: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: '#fff' }}>
              Primeros pasos{organizationName ? ` en ${organizationName}` : ''}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', mt: 0.25 }}>
              {completed} de {steps.length} completados. Termina la configuración para empezar a vender.
            </Typography>
          </Box>
          <Tooltip title="Ocultar">
            <IconButton size="small" onClick={dismiss} sx={{ color: 'rgba(255,255,255,0.7)' }} aria-label="Ocultar primeros pasos">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={(completed / steps.length) * 100}
          sx={{ mt: 2, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.12)', '& .MuiLinearProgress-bar': { bgcolor: TRUE_COLORS.secondary, borderRadius: 3 } }}
        />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' } }}>
        {steps.map((step, i) => {
          const isNext = step.key === next?.key;
          return (
            <Box
              key={step.key}
              sx={{
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                borderTop: { xs: i > 0 ? `1px solid ${TRUE_COLORS.borderSolid}` : 'none', xl: 'none' },
                borderLeft: { xl: i > 0 ? `1px solid ${TRUE_COLORS.borderSolid}` : 'none' },
                bgcolor: isNext ? 'rgba(111,137,148,0.06)' : 'transparent',
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {step.done
                  ? <CheckCircleIcon sx={{ color: TRUE_COLORS.success, fontSize: 20 }} />
                  : <RadioButtonUncheckedIcon sx={{ color: TRUE_COLORS.beige, fontSize: 20 }} />}
                <Typography sx={{
                  fontWeight: 600, fontSize: '0.9rem',
                  color: step.done ? 'text.secondary' : 'text.primary',
                  textDecoration: step.done ? 'line-through' : 'none',
                }}>
                  {step.title}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary', flexGrow: 1, pl: 3.5 }}>
                {step.description}
              </Typography>
              {!step.done && (
                <Box sx={{ pl: 3.5 }}>
                  <Button
                    size="small"
                    variant={isNext ? 'contained' : 'text'}
                    sx={isNext ? undefined : { ml: -1 }}
                    endIcon={<ChevronRightIcon />}
                    onClick={() => navigate(step.to)}
                  >
                    {step.cta}
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Card>
  );
};

export default GettingStarted;
